import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import ChatArea from "../components/ChatArea";
import { Menu, X, PanelLeftClose, PanelLeft, Eye, LogOut, ArrowLeft } from "lucide-react";

// Using relative URL for API calls
const API = "/api";

// Emulation Banner Component
const EmulationBanner = ({ userEmail, onExit }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 flex items-center justify-between shadow-lg">
    <div className="flex items-center gap-3">
      <Eye className="w-5 h-5" />
      <span className="font-medium">Emulation Mode</span>
      <span className="text-blue-100">Viewing as: <strong>{userEmail}</strong></span>
    </div>
    <button
      onClick={onExit}
      className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-lg transition-colors"
    >
      <ArrowLeft className="w-4 h-4" />
      Return to Admin
    </button>
  </div>
);

export default function Chat() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [conversationUsage, setConversationUsage] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [isEmulating, setIsEmulating] = useState(false);
  const [emulatedUserEmail, setEmulatedUserEmail] = useState("");

  // Check for emulation mode
  useEffect(() => {
    const emulationMode = localStorage.getItem("emulation_mode");
    const emulatedEmail = localStorage.getItem("emulated_user_email");
    if (emulationMode === "true" && emulatedEmail) {
      setIsEmulating(true);
      setEmulatedUserEmail(emulatedEmail);
    }
  }, []);

  // Exit emulation mode
  const handleExitEmulation = () => {
    // Restore admin tokens
    const originalToken = localStorage.getItem("admin_original_token");
    const originalEmail = localStorage.getItem("admin_original_email");
    
    // Clear emulation data
    localStorage.removeItem("emulation_mode");
    localStorage.removeItem("emulated_user_email");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    
    // Restore admin session
    if (originalToken) {
      localStorage.setItem("admin_token", originalToken);
    }
    if (originalEmail) {
      localStorage.setItem("admin_email", originalEmail);
    }
    
    // Clean up original tokens
    localStorage.removeItem("admin_original_token");
    localStorage.removeItem("admin_original_email");
    
    toast.success("Returned to admin panel");
    navigate("/admin/users");
  };

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem("auth_token");
  }, []);

  // Fetch user info
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Handle user update from settings/upgrade
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch conversation usage (for free plan limits) - defined before the auto-refresh useEffect
  const fetchConversationUsage = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/conversations/usage`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        const data = await response.json();
        setConversationUsage(data);
      }
    } catch (error) {
      console.error("Error fetching conversation usage:", error);
    }
  }, [getAuthToken]);

  // Auto-refresh conversation usage on window focus and periodically
  useEffect(() => {
    // Refresh when window gains focus (user comes back to tab)
    const handleFocus = () => {
      fetchConversationUsage();
    };
    
    window.addEventListener("focus", handleFocus);
    
    // Also poll every 60 seconds for real-time feel
    const interval = setInterval(() => {
      fetchConversationUsage();
    }, 60000);
    
    return () => {
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, [fetchConversationUsage]);

  // Fetch conversations (optionally filtered by project)
  const fetchConversations = useCallback(async (projectId = null) => {
    try {
      const token = getAuthToken();
      const url = projectId 
        ? `${API}/conversations?project_id=${projectId}`
        : `${API}/conversations`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          navigate("/login");
          return;
        }
        throw new Error("Failed to fetch conversations");
      }

      const data = await response.json();
      setConversations(data);
      return data;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      return [];
    }
  }, [getAuthToken, navigate]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (convId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (!response.ok) {
        throw new Error("Failed to fetch messages");
      }

      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, [getAuthToken]);

  // Initial load - only runs once when conversationId changes (from URL)
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await fetchConversationUsage();
      
      if (conversationId) {
        // Load specific conversation first
        try {
          const token = getAuthToken();
          const convRes = await fetch(`${API}/conversations/${conversationId}`, {
            headers: { Authorization: `Bearer ${token}` },
            
          });
          
          if (convRes.ok) {
            const conv = await convRes.json();
            setCurrentConversation(conv);
            
            // If the conversation belongs to a project, load that project's context
            if (conv.project_id) {
              try {
                const projRes = await fetch(`${API}/projects/${conv.project_id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (projRes.ok) {
                  const projData = await projRes.json();
                  setCurrentProject(projData);
                  // Fetch conversations for this project
                  await fetchConversations(conv.project_id);
                }
              } catch (e) {
                console.error("Error fetching project:", e);
              }
            } else {
              // Non-project conversation - fetch general chats
              setCurrentProject(null);
              await fetchConversations(null);
            }
            
            await fetchMessages(conversationId);
          } else {
            navigate("/chat", { replace: true });
          }
        } catch (e) {
          console.error("Error loading conversation:", e);
          navigate("/chat", { replace: true });
        }
      } else {
        // No specific conversation - load based on current project or general
        await fetchConversations(currentProject?.project_id || null);
      }
      
      setLoading(false);
    };

    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Create new conversation (optionally in a project)
  const handleNewChat = async (projectId = null) => {
    try {
      const token = getAuthToken();
      const body = { title: "New Chat" };
      if (projectId) {
        body.project_id = projectId;
      }
      
      const response = await fetch(`${API}/conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        
      });

      if (!response.ok) {
        throw new Error("Failed to create conversation");
      }

      const newConv = await response.json();
      
      // Add to conversations list (it will be filtered correctly since we're in the right project context)
      setConversations(prev => [newConv, ...prev]);
      setCurrentConversation(newConv);
      setMessages([]);
      navigate(`/chat/${newConv.conversation_id}`);
      
      // Refresh conversation usage after creating
      await fetchConversationUsage();
      
      if (isMobile) {
        setSidebarOpen(false);
      }
    } catch (error) {
      toast.error("Failed to create new chat");
    }
  };

  // Handle project selection
  const handleSelectProject = async (project) => {
    // Clear state immediately
    setConversations([]);
    setCurrentConversation(null);
    setMessages([]);
    setCurrentProject(project);
    setLoading(true);
    
    try {
      if (project) {
        // Fetch conversations for this project only
        const convs = await fetchConversations(project.project_id);
        console.log(`Fetched ${convs?.length || 0} conversations for project ${project.project_id}`);
      } else {
        // Fetch non-project conversations only
        const convs = await fetchConversations(null);
        console.log(`Fetched ${convs?.length || 0} general conversations`);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
    
    setLoading(false);
    navigate("/chat");
  };

  // Create new chat in a project
  const handleNewProjectChat = async (project) => {
    setCurrentProject(project);
    // First fetch the project's existing conversations
    await fetchConversations(project.project_id);
    // Then create a new chat in the project
    await handleNewChat(project.project_id);
  };

  // Select conversation
  const handleSelectConversation = async (conv) => {
    setCurrentConversation(conv);
    navigate(`/chat/${conv.conversation_id}`);
    await fetchMessages(conv.conversation_id);
    
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (convId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/conversations/${convId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (!response.ok) {
        throw new Error("Failed to delete conversation");
      }

      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      
      if (currentConversation?.conversation_id === convId) {
        setCurrentConversation(null);
        setMessages([]);
        navigate("/chat");
      }
      
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  // Star/Unstar conversation
  const handleStarConversation = async (convId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/conversations/${convId}/star`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (!response.ok) {
        throw new Error("Failed to update conversation");
      }

      const data = await response.json();
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === convId ? { ...c, starred: data.starred } : c
        )
      );
      
      if (currentConversation?.conversation_id === convId) {
        setCurrentConversation(prev => ({ ...prev, starred: data.starred }));
      }
    } catch (error) {
      toast.error("Failed to update conversation");
    }
  };

  // Rename conversation
  const handleRenameConversation = async (convId, newTitle) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/conversations/${convId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: newTitle }),
        
      });

      if (!response.ok) {
        throw new Error("Failed to rename conversation");
      }

      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === convId ? { ...c, title: newTitle } : c
        )
      );
      
      if (currentConversation?.conversation_id === convId) {
        setCurrentConversation(prev => ({ ...prev, title: newTitle }));
      }
      
      toast.success("Conversation renamed");
    } catch (error) {
      toast.error("Failed to rename conversation");
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      const token = getAuthToken();
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      navigate("/");
    }
  };

  // Update conversation title after first message
  const updateConversationTitle = (convId, title) => {
    setConversations(prev => prev.map(c => 
      c.conversation_id === convId ? { ...c, title } : c
    ));
    if (currentConversation?.conversation_id === convId) {
      setCurrentConversation(prev => ({ ...prev, title }));
    }
  };

  return (
    <div className={`h-screen flex overflow-hidden bg-[#0D0D0D] ${isEmulating ? 'pt-12' : ''}`} data-testid="chat-page">
      {/* Emulation Banner */}
      {isEmulating && (
        <EmulationBanner userEmail={emulatedUserEmail} onExit={handleExitEmulation} />
      )}
      
      {/* Mobile menu button */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`fixed ${isEmulating ? 'top-16' : 'top-4'} left-4 z-50 p-2 rounded-lg bg-[#171717] border border-[#27272A] text-white hover:bg-[#1E1E1E] transition-colors`}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        ${isMobile ? 'fixed inset-y-0 left-0 z-40' : 'relative'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden'}
        transition-all duration-300 ease-in-out
      `}>
        <Sidebar
          conversations={conversations}
          currentConversation={currentConversation}
          onNewChat={() => handleNewChat(currentProject?.project_id)}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onStarConversation={handleStarConversation}
          onRenameConversation={handleRenameConversation}
          onLogout={handleLogout}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          user={user}
          loading={loading}
          onUserUpdate={handleUserUpdate}
          conversationUsage={conversationUsage}
          currentProject={currentProject}
          onSelectProject={handleSelectProject}
          onNewProjectChat={handleNewProjectChat}
          refreshConversations={() => fetchConversations(currentProject?.project_id)}
        />
      </div>

      {/* Sidebar Toggle Button (when collapsed on desktop) */}
      {!isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#1E1E1E] text-[#888] hover:text-white hover:bg-[#2A2A2A] transition-colors"
          data-testid="expand-sidebar-btn"
        >
          <PanelLeft className="w-5 h-5" />
        </button>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <ChatArea
          conversation={currentConversation}
          messages={messages}
          setMessages={setMessages}
          onNewChat={handleNewChat}
          updateConversationTitle={updateConversationTitle}
          refreshConversations={fetchConversations}
          setCurrentConversation={setCurrentConversation}
          user={user}
        />
      </div>
    </div>
  );
}
